<template>
  <div>
    <v-alert
      v-if="erro"
      type="error"
      dense
      outlined
      role="alert"
    >
      {{ erro }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :header-props="{ sortByText: 'Ordenar por' }"
      :footer-props="{ itemsPerPageText: 'Itens por página' }"
      loading-text="Carregando relatórios..."
      no-data-text="Nenhum relatório IeA solicitado"
      mobile-breakpoint="900"
    >
      <template v-slot:[`item.tipo`]="{ item }">
        {{ tipos[item.tipo] || item.tipo }}
      </template>
      <template v-slot:[`item.created`]="{ item }">
        {{ formatarData(item.created) }}
      </template>
      <template v-slot:[`item.processado_em`]="{ item }">
        {{ formatarData(item.processado_em) }}
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <general-status :status="item.status" />
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex">
          <v-btn
            icon
            small
            :disabled="item.status !== 'SUCESSO' || !!downloads[item.id]"
            :loading="!!downloads[item.id]"
            :aria-label="`Baixar relatório ${item.id}`"
            title="Baixar relatório"
            @click="baixar(item)"
          >
            <v-icon small>
              mdi-download
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :aria-label="`Detalhes do relatório ${item.id}`"
            title="Detalhes do processamento"
            @click="selecionado = item.id"
          >
            <v-icon small>
              mdi-file-find-outline
            </v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`footer.page-text`]="props">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="carregar"
            >
              mdi-refresh
            </v-icon>
          </template>
          <span>Clique aqui para recarregar os relatórios</span>
        </v-tooltip>
        <span class="ml-4">{{ props.pageStart }}-{{ props.pageStop }} de {{ props.itemsLength }}</span>
      </template>
    </v-data-table>
    <general-dialog-detalhes
      :title="detalhes ? `Detalhes do Relatório IeA #${detalhes.id}` : ''"
      :show-dialog="!!detalhes"
      max-width="1200px"
      @changeValue="!$event && (selecionado = null)"
    >
      <template slot="body">
        <v-data-table
          :headers="resumoHeaders"
          :items="resumoItens"
          hide-default-footer
          disable-pagination
          class="elevation-1 style-table-dialog-detalhes"
          mobile-breakpoint="10"
        />
      </template>
    </general-dialog-detalhes>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import RelatoriosAieService from '@/services/RelatoriosAieService';

export default {
  name: 'AieConsultas',

  components: {
    GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
    GeneralDialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes')
  },

  data() {
    return {
      items: [],
      loading: false,
      erro: '',
      downloads: {},
      selecionado: null,
      timer: null,
      ativo: true,
      tipos: { AIE: 'Relatório IeA', HISTORICO_JUSTIFICATIVAS: 'Histórico de Justificativas' },
      resumoHeaders: [
        { text: 'Resumo', value: 'parametro', sortable: false },
        { text: 'Valor', value: 'valor', sortable: false }
      ],
      headers: [
        { text: '#', value: 'id' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Usuário', value: 'usuario.nome' },
        { text: 'Solicitado em', value: 'created' },
        { text: 'Concluído em', value: 'processado_em' },
        { text: 'Status', value: 'status' },
        { text: 'PEPs', value: 'qtd_peps' },
        { text: 'Ações', value: 'actions', sortable: false }
      ]
    };
  },

  computed: {
    detalhes() {
      return this.items.find((item) => item.id === this.selecionado);
    },

    resumoItens() {
      const item = this.detalhes;
      if (!item) return [];
      return [
        { parametro: 'ID', valor: item.id },
        { parametro: 'TIPO', valor: this.tipos[item.tipo] || item.tipo },
        { parametro: 'USUARIO', valor: item.usuario ? item.usuario.nome : '-' },
        { parametro: 'STATUS', valor: item.status },
        { parametro: 'SOLICITADO EM', valor: this.formatarData(item.created) },
        { parametro: 'CONCLUIDO EM', valor: this.formatarData(item.processado_em) },
        { parametro: 'REFERENCIA DOS AGINGS', valor: this.formatarReferencia(item.referencia) },
        { parametro: 'PEPS SOLICITADOS', valor: item.qtd_peps },
        { parametro: 'LINHAS GERADAS', valor: item.qtd_linhas },
        { parametro: 'RESUMO', valor: item.resumo || '-' }
      ];
    }
  },

  mounted() {
    this.carregar();
  },

  beforeDestroy() {
    this.ativo = false;
    clearTimeout(this.timer);
  },

  methods: {
    formatarData(data) {
      return data ? dayjs(data).format('DD/MM/YYYY HH:mm') : '-';
    },

    formatarReferencia(data) {
      return data ? dayjs(data).format('MM/YYYY') : '-';
    },

    async carregar() {
      if (this.loading || !this.ativo) return;
      clearTimeout(this.timer);
      this.loading = true;
      try {
        const response = await RelatoriosAieService.historico();
        this.items = response.data.data || [];
        this.erro = '';
      } catch (error) {
        this.erro = 'Não foi possível atualizar o histórico. Tentaremos novamente em alguns segundos.';
      } finally {
        this.loading = false;
        if (this.ativo) this.timer = setTimeout(() => this.carregar(), 15000);
      }
    },

    async baixar(item) {
      if (item.status !== 'SUCESSO' || this.downloads[item.id]) return;
      this.$set(this.downloads, item.id, true);
      try {
        const response = await RelatoriosAieService.baixar(item.id);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = item.tipo === 'HISTORICO_JUSTIFICATIVAS'
          ? `historico_justificativas_iea_${item.id}.xlsx`
          : `relatorio_iea_${item.id}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error('Não foi possível baixar o relatório. Atualize o histórico e tente novamente.', '', { position: 'topRight' });
      } finally {
        this.$delete(this.downloads, item.id);
      }
    }
  }
};
</script>
